// ═══════════════════════════════════════════════════════════
// THE LIFE NINJA GAME — editable content.
// Edit territories, stops, links & elements here; the program
// page updates automatically. `free: true` = visible to all,
// otherwise stops are locked until the client has access.
// ═══════════════════════════════════════════════════════════

export const BOARD_IMAGE = 'https://customer-assets-v7afamib.emergentagent.net/job_9cc27ad2-9473-4d8d-951e-838ca155566b/artifacts/odrleuev_ChatGPT%20Image%20Sep%207%2C%202026%2C%2011_33_15%20AM.png';

export const gameIntro = {
  title: 'The Life Ninja Game',
  tagline: 'A Journey of Choice, a Practice for Life',
  subtitle: 'Navigate. Learn. Level Up. Live Consciously.',
  note: "This isn't a race. It's a practice. Every step counts. Every choice matters. You are becoming.",
};

export const howToPlay = [
  'Discover where you are now',
  'Choose your next step',
  'Use tools, allies & practices',
  'Reflect, integrate & level up',
  'Life happens — adapt & keep going',
];

export const remember = [
  'No path is wrong.',
  'You can change direction.',
  'You are always growing.',
  'You are not alone.',
  'You are the hero of your life.',
];

export const territories = [
  {
    id: 'survive',
    level: 1,
    name: 'Survive',
    tagline: "Something isn't working.",
    color: '#B0413E',
    free: true,
    stops: [
      { n: 1, title: 'Where Am I?' },
      { n: 2, title: 'Wake-Up Call' },
      { n: 3, title: 'The Fog' },
      { n: 4, title: 'The Crisis' },
      { n: 5, title: 'Safety First' },
    ],
  },
  {
    id: 'stabilize',
    level: 2,
    name: 'Stabilize',
    tagline: 'Before we build, we regulate.',
    color: '#D98A3D',
    free: false,
    stops: [
      { n: 6, title: 'Meet Your Nervous System' },
      { n: 7, title: 'Get Out of Emergency Mode' },
      { n: 8, title: 'Sleep & Rhythm', link: '/programs/terrain', linkLabel: 'Explore SLOW WELLNESS' },
      { n: 9, title: 'Nourish', link: '/programs/flex-detox', linkLabel: 'Explore the Flex Detox' },
      { n: 10, title: 'Reduce the Load', link: '/programs/crap-cleanse', linkLabel: 'Explore the C.R.A.P. Cleanse' },
    ],
  },
  {
    id: 'restore',
    level: 3,
    name: 'Restore',
    tagline: 'Rebuild capacity.',
    color: '#C9A227',
    free: false,
    stops: [
      { n: 11, title: 'Move Again', link: '/programs/back-reset', linkLabel: 'Explore the Back Reset' },
      { n: 12, title: 'Feel the Body' },
      { n: 13, title: 'Release', link: '/programs/fascia-reset', linkLabel: 'Explore the 28 Day Stress Reset' },
      { n: 14, title: 'Digest', link: '/programs/feel-good-detox', linkLabel: 'Explore the Feel Good Detox' },
      { n: 15, title: 'Rebuild', link: '/programs/double-down', linkLabel: 'Explore the Double Down' },
    ],
  },
  {
    id: 'reconnect',
    level: 4,
    name: 'Reconnect',
    tagline: 'Come home to yourself.',
    color: '#6E9A5E',
    free: false,
    stops: [
      { n: 16, title: 'Come Back to Yourself' },
      { n: 17, title: 'Feel' },
      { n: 18, title: 'Grief' },
      { n: 19, title: 'Patterns' },
      { n: 20, title: 'Beliefs' },
    ],
  },
  {
    id: 'choose',
    level: 5,
    name: 'Choose',
    tagline: 'You have choices.',
    color: '#5B86A8',
    free: false,
    stops: [
      { n: 21, title: 'Agency' },
      { n: 22, title: 'Values' },
      { n: 23, title: 'Boundaries' },
      { n: 24, title: 'Relationships' },
      { n: 25, title: 'Environment' },
    ],
  },
  {
    id: 'create',
    level: 6,
    name: 'Create',
    tagline: 'Design your life.',
    color: '#7E6BA6',
    free: false,
    stops: [
      { n: 26, title: 'Vision' },
      { n: 27, title: 'Life Force' },
      { n: 28, title: 'Conscious Consumption' },
      { n: 29, title: 'Daily Choices' },
      { n: 30, title: 'Personal Practice', link: '/programs/vip', linkLabel: 'Explore VIP Coaching' },
    ],
  },
  {
    id: 'thrive',
    level: 7,
    name: 'Thrive',
    tagline: 'Inhabit. Expand. Contribute.',
    color: '#4C4A7A',
    free: false,
    stops: [
      { n: 31, title: 'Self-Trust' },
      { n: 32, title: 'Self-Leadership' },
      { n: 33, title: 'Resilience' },
      { n: 34, title: 'Adaptation' },
      { n: 35, title: 'Purpose', link: '/programs/vip/coaching', linkLabel: 'Explore the Four Pillars' },
      { n: 36, title: 'Expression' },
      { n: 37, title: 'Connection' },
      { n: 38, title: 'Contribution' },
      { n: 39, title: 'Expansion' },
      { n: 40, title: 'The Great Work' },
    ],
  },
];

export const gameElements = [
  { name: 'Stops', desc: 'Places you arrive.' },
  { name: 'Crossroads', desc: 'Places where you choose.' },
  { name: 'Loops', desc: 'Patterns you repeat.' },
  { name: 'Spirals', desc: 'Revisit at a higher level.' },
  { name: 'Blocks', desc: 'Things preventing movement.' },
  { name: 'Energy Drains', desc: 'Things consuming life force.' },
  { name: 'Power-Ups', desc: 'Practices that increase capacity.' },
  { name: 'Compass Points', desc: 'Values, intuition, inner knowing.' },
  { name: 'Growth Zones', desc: 'Places to experiment.' },
  { name: 'Dragons', desc: 'The big things we avoid. Face. Feel. Freedom.' },
  { name: 'Leveling Up', desc: 'Grow. Evolve. Expand.' },
  { name: 'Boosters', desc: 'Tools, allies, support.' },
  { name: 'Reflection Bus', desc: 'Pause. Reflect. Recognize.' },
];
